/**
 * Excel Export Utility
 * Handles Excel export with Arabic RTL support
 *
 * Note: The community xlsx library doesn't support cell styling (bold, colors).
 * RTL and column widths are fully supported.
 */
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Platform } from 'react-native';

// Column configuration interface
interface ColumnConfig {
  header: string;      // Arabic header name
  key: string;         // Data key
  width?: number;      // Column width (in characters)
}

// Export options interface
interface ExportOptions {
  filename: string;
  sheetName?: string;
  columns: ColumnConfig[];
  data: any[];
  rtl?: boolean;       // Right-to-left (default: true for Arabic)
}

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue(obj, 'user.name') returns obj.user.name
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : '';
  }, obj);
};

/**
 * Format value for Excel
 */
const formatValue = (value: any): string | number => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'نعم' : 'لا';
  }
  if (value instanceof Date) {
    return value.toLocaleDateString('ar-AE');
  }
  if (typeof value === 'number') {
    return value;
  }
  return String(value);
};

/**
 * Calculate optimal column width for Arabic text
 * Arabic characters are typically wider than Latin characters
 */
const calculateColumnWidth = (header: string, data: any[], key: string, defaultWidth?: number): number => {
  if (defaultWidth) return defaultWidth;

  // Start with header width (Arabic chars need ~1.8x width)
  let maxWidth = header.length * 1.8;

  // Check data values
  data.forEach(item => {
    const value = getNestedValue(item, key);
    if (value !== null && value !== undefined) {
      const strValue = String(value);
      // Arabic text needs more width
      const isArabic = /[\u0600-\u06FF]/.test(strValue);
      const charWidth = isArabic ? 1.8 : 1;
      const valueWidth = strValue.length * charWidth;
      maxWidth = Math.max(maxWidth, valueWidth);
    }
  });

  // Clamp between reasonable bounds
  return Math.min(Math.max(maxWidth, 12), 60);
};

/**
 * Export data to Excel file with Arabic RTL support
 */
export const exportToExcel = ({
  filename,
  sheetName = 'Sheet1',
  columns,
  data,
  rtl = true,
}: ExportOptions): void => {
  try {
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();

    // Prepare header row
    const headers = columns.map(col => col.header);

    // Prepare data rows
    const rows = data.map(item => {
      return columns.map(col => {
        const value = getNestedValue(item, col.key);
        return formatValue(value);
      });
    });

    // Combine headers and data
    const worksheetData = [headers, ...rows];

    // Create worksheet from array of arrays
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths with better Arabic support
    const colWidths = columns.map(col => ({
      wch: calculateColumnWidth(col.header, data, col.key, col.width)
    }));
    worksheet['!cols'] = colWidths;

    // Set RTL direction for Arabic - using the correct views approach
    if (rtl) {
      // Initialize views array if not present
      if (!worksheet['!views']) {
        worksheet['!views'] = [{}];
      }
      // Set rightToLeft property on the first view
      (worksheet['!views'] as any[])[0].rightToLeft = true;
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Set workbook properties for better Arabic support
    if (!workbook.Workbook) workbook.Workbook = {};
    if (!workbook.Workbook.Views) workbook.Workbook.Views = [{}];
    if (rtl) {
      (workbook.Workbook.Views[0] as any).RTL = true;
    }

    // Generate file and download
    if (Platform.OS === 'web') {
      // For web: use file-saver
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `${filename}.xlsx`);
    } else {
      // For mobile: would need expo-file-system or similar
      console.warn('Excel export on mobile requires additional setup');
    }
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
};

/**
 * Create a worksheet from columns and data
 */
const createWorksheet = (columns: ColumnConfig[], data: any[], rtl = true): XLSX.WorkSheet => {
  const headers = columns.map(col => col.header);
  const rows = data.map(item =>
    columns.map(col => formatValue(getNestedValue(item, col.key)))
  );
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  worksheet['!cols'] = columns.map(col => ({
    wch: calculateColumnWidth(col.header, data, col.key, col.width)
  }));
  if (rtl) {
    if (!worksheet['!views']) worksheet['!views'] = [{}];
    (worksheet['!views'] as any[])[0].rightToLeft = true;
  }
  return worksheet;
};

/**
 * Export multiple sheets to a single Excel file
 */
interface SheetConfig {
  sheetName: string;
  columns: ColumnConfig[];
  data: any[];
}

export const exportMultiSheetToExcel = (filename: string, sheets: SheetConfig[], rtl = true): void => {
  try {
    const workbook = XLSX.utils.book_new();
    for (const sheet of sheets) {
      const worksheet = createWorksheet(sheet.columns, sheet.data, rtl);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.sheetName);
    }
    if (!workbook.Workbook) workbook.Workbook = {};
    if (!workbook.Workbook.Views) workbook.Workbook.Views = [{}];
    if (rtl) {
      (workbook.Workbook.Views[0] as any).RTL = true;
    }
    if (Platform.OS === 'web') {
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `${filename}.xlsx`);
    }
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
};

// ============================================================
// Pre-configured export functions for different entities
// ============================================================

/**
 * Export Goals to Excel
 */
export const exportGoalsToExcel = (
  goals: any[],
  kpis: any[] = [],
  filename = 'الأهداف_الاستراتيجية',
) => {
  const sheets: SheetConfig[] = [
    {
      sheetName: 'الأهداف',
      columns: [
        { header: 'عنوان الهدف', key: 'title', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الجهة', key: 'organization_name', width: 25 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'الحالة', key: 'status_display', width: 15 },
        { header: 'تاريخ البداية', key: 'start_date', width: 15 },
        { header: 'تاريخ الانتهاء', key: 'target_completion_date', width: 15 },
      ],
      data: goals.map(goal => ({
        ...goal,
        status_display: getGoalStatusDisplay(goal.status),
      })),
    },
  ];

  if (kpis.length > 0) {
    sheets.push({
      sheetName: 'المؤشرات',
      columns: [
        { header: 'الهدف الاستراتيجي', key: 'goal_title', width: 35 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'معادلة القياس', key: 'measurement_formula', width: 30 },
      ],
      data: kpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  exportMultiSheetToExcel(filename, sheets);
};

/**
 * Export Initiatives to Excel
 */
export const exportInitiativesToExcel = (
  initiatives: any[],
  kpis: any[] = [],
  activities: any[] = [],
  activityKpis: any[] = [],
  filename = 'المبادرات',
) => {
  const sheets: SheetConfig[] = [
    {
      sheetName: 'المبادرات',
      columns: [
        { header: 'عنوان المبادرة', key: 'title', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الهدف الاستراتيجي', key: 'strategic_goal_title', width: 30 },
        { header: 'الجهة', key: 'department_name', width: 25 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'عدد المؤشرات', key: 'kpi_count', width: 15 },
        { header: 'عدد الأنشطة', key: 'activity_count', width: 15 },
        { header: 'تاريخ البداية', key: 'start_date', width: 15 },
        { header: 'تاريخ الانتهاء', key: 'end_date', width: 15 },
      ],
      data: initiatives,
    },
  ];

  if (kpis.length > 0) {
    sheets.push({
      sheetName: 'مؤشرات المبادرات',
      columns: [
        { header: 'المبادرة', key: 'initiative_title', width: 35 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'معادلة القياس', key: 'measurement_formula', width: 30 },
      ],
      data: kpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  if (activities.length > 0) {
    sheets.push({
      sheetName: 'الأنشطة',
      columns: [
        { header: 'المبادرة', key: 'initiative_title', width: 35 },
        { header: 'اسم النشاط', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'الحالة', key: 'status_display', width: 15 },
        { header: 'تاريخ البداية', key: 'start_date', width: 15 },
        { header: 'تاريخ الانتهاء', key: 'end_date', width: 15 },
      ],
      data: activities.map(activity => ({
        ...activity,
        status_display: getInitiativeStatusDisplay(activity.status),
      })),
    });
  }

  if (activityKpis.length > 0) {
    sheets.push({
      sheetName: 'مؤشرات الأنشطة',
      columns: [
        { header: 'المبادرة', key: 'initiative_title', width: 30 },
        { header: 'النشاط', key: 'activity_title', width: 30 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
      ],
      data: activityKpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  exportMultiSheetToExcel(filename, sheets);
};

/**
 * Export KPIs to Excel
 */
export const exportKPIsToExcel = (kpis: any[], filename = 'مؤشرات_الأداء') => {
  exportToExcel({
    filename,
    sheetName: 'المؤشرات',
    columns: [
      { header: 'اسم المؤشر', key: 'name', width: 40 },
      { header: 'الوصف', key: 'description', width: 50 },
      { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
      { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
      { header: 'القيمة المحققة', key: 'achieved_value', width: 15 },
      { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      { header: 'وحدة القياس', key: 'unit', width: 15 },
      { header: 'التكرار', key: 'frequency_display', width: 15 },
      { header: 'الاتجاه', key: 'polarity_display', width: 15 },
      { header: 'الوزن', key: 'weight', width: 10 },
    ],
    data: kpis.map(kpi => ({
      ...kpi,
      frequency_display: getFrequencyDisplay(kpi.frequency),
      polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
    })),
  });
};

/**
 * Export KPI Readings to Excel
 */
export const exportKPIReadingsToExcel = (readings: any[], kpiName: string, filename = 'قراءات_المؤشر') => {
  exportToExcel({
    filename: `${filename}_${kpiName}`,
    sheetName: 'القراءات',
    columns: [
      { header: 'تاريخ القياس', key: 'measurement_date', width: 15 },
      { header: 'القيمة', key: 'value', width: 15 },
      { header: 'المستهدف', key: 'target_value', width: 15 },
      { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
      { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      { header: 'الملاحظات', key: 'notes', width: 40 },
      { header: 'تاريخ الإدخال', key: 'created_at', width: 15 },
    ],
    data: readings,
  });
};

/**
 * Export Employees to Excel
 */
export const exportEmployeesToExcel = (
  employees: any[],
  kpis: any[] = [],
  filename = 'الموظفين',
) => {
  const sheets: SheetConfig[] = [
    {
      sheetName: 'الموظفين',
      columns: [
        { header: 'رقم الموظف', key: 'employee_number', width: 15 },
        { header: 'الاسم بالعربية', key: 'name_ar', width: 25 },
        { header: 'الاسم بالإنجليزية', key: 'name_en', width: 25 },
        { header: 'المسمى الوظيفي', key: 'position', width: 25 },
        { header: 'الوحدة التنظيمية', key: 'organizational_unit_name', width: 25 },
        { header: 'البريد الإلكتروني', key: 'email', width: 30 },
        { header: 'رقم الهاتف', key: 'phone', width: 15 },
        { header: 'الحالة', key: 'status_display', width: 12 },
        { header: 'نسبة الإنجاز الكلية', key: 'overall_achievement', width: 18 },
        { header: 'تاريخ التعيين', key: 'hire_date', width: 15 },
      ],
      data: employees.map(emp => ({
        ...emp,
        organizational_unit_name:
          emp.organizational_unit_name_ar || emp.organizational_unit_name || '',
        status_display: emp.is_active ? 'نشط' : 'غير نشط',
      })),
    },
  ];

  if (kpis.length > 0) {
    sheets.push({
      sheetName: 'المؤشرات',
      columns: [
        { header: 'الموظف', key: 'employee_name', width: 30 },
        { header: 'المسمى الوظيفي', key: 'employee_position', width: 25 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
      ],
      data: kpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  exportMultiSheetToExcel(filename, sheets);
};

/**
 * Export Organizations Overview to Excel
 */
export const exportOrganizationsToExcel = (organizations: any[], filename = 'الجهات') => {
  exportToExcel({
    filename,
    sheetName: 'الجهات',
    columns: [
      { header: 'اسم الجهة', key: 'name', width: 30 },
      { header: 'عدد الأهداف', key: 'total_goals', width: 15 },
      { header: 'عدد المبادرات', key: 'total_initiatives', width: 15 },
      { header: 'عدد المؤشرات', key: 'total_kpis', width: 15 },
      { header: 'مؤشرات جيدة', key: 'kpis_good', width: 15 },
      { header: 'مؤشرات متوسطة', key: 'kpis_average', width: 15 },
      { header: 'مؤشرات ضعيفة', key: 'kpis_poor', width: 15 },
      { header: 'نسبة تحقيق الرؤية', key: 'vision_achievement', width: 18 },
      { header: 'مؤشر الالتزام', key: 'commitment_index', width: 15 },
    ],
    data: organizations,
  });
};

/**
 * Export Services to Excel
 */
export const exportServicesToExcel = (
  services: any[],
  kpis: any[] = [],
  filename = 'الخدمات',
) => {
  const sheets: SheetConfig[] = [
    {
      sheetName: 'الخدمات',
      columns: [
        { header: 'اسم الخدمة', key: 'name', width: 30 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الوحدة التنظيمية', key: 'owning_units_names', width: 25 },
        { header: 'عدد المؤشرات', key: 'kpis_count', width: 15 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      ],
      data: services.map(s => ({
        ...s,
        owning_units_names: (s.owning_units_details || [])
          .map((u: any) => u.name_ar || u.name)
          .join('، ') || '',
      })),
    },
  ];

  if (kpis.length > 0) {
    sheets.push({
      sheetName: 'المؤشرات',
      columns: [
        { header: 'الخدمة', key: 'service_name', width: 35 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'معادلة القياس', key: 'measurement_formula', width: 30 },
      ],
      data: kpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  exportMultiSheetToExcel(filename, sheets);
};

/**
 * Export Processes to Excel
 */
export const exportProcessesToExcel = (
  processes: any[],
  kpis: any[] = [],
  subProcesses: any[] = [],
  subProcessKpis: any[] = [],
  filename = 'العمليات',
) => {
  const sheets: SheetConfig[] = [
    {
      sheetName: 'العمليات',
      columns: [
        { header: 'اسم العملية', key: 'name', width: 30 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الهدف الاستراتيجي', key: 'strategic_goal_title', width: 30 },
        { header: 'الوحدة التنظيمية', key: 'organizational_unit_names', width: 25 },
        { header: 'عدد العمليات الفرعية', key: 'sub_processes_count', width: 20 },
        { header: 'عدد المؤشرات', key: 'kpis_count', width: 15 },
      ],
      data: processes.map(p => ({
        ...p,
        organizational_unit_names: (p.organizational_units_details || [])
          .map((u: any) => u.name_ar || u.name)
          .join('، ') || '',
      })),
    },
  ];

  if (kpis.length > 0) {
    sheets.push({
      sheetName: 'مؤشرات العمليات',
      columns: [
        { header: 'العملية', key: 'process_name', width: 35 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'معادلة القياس', key: 'measurement_formula', width: 30 },
      ],
      data: kpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  if (subProcesses.length > 0) {
    sheets.push({
      sheetName: 'العمليات الفرعية',
      columns: [
        { header: 'العملية الرئيسية', key: 'process_name', width: 30 },
        { header: 'اسم العملية الفرعية', key: 'name', width: 30 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الهدف الاستراتيجي', key: 'strategic_goal_title', width: 30 },
        { header: 'الوحدة التنظيمية', key: 'organizational_unit_names', width: 25 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'زمن العملية', key: 'duration_display', width: 15 },
        { header: 'عدد الموظفين', key: 'employees_count', width: 15 },
        { header: 'تكرار التنفيذ', key: 'execution_frequency', width: 15 },
        { header: 'تكلفة العملية', key: 'cost', width: 15 },
        { header: 'المسؤول عن الاعتماد', key: 'approval_person', width: 20 },
        { header: 'عدد الخطوات', key: 'steps_count', width: 15 },
        { header: 'عدد المؤشرات', key: 'kpis_count', width: 15 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      ],
      data: subProcesses.map(sp => ({
        ...sp,
        organizational_unit_names: (sp.organizational_units_details || [])
          .map((u: any) => u.name_ar || u.name)
          .join('، ') || '',
      })),
    });
  }

  if (subProcessKpis.length > 0) {
    sheets.push({
      sheetName: 'مؤشرات العمليات الفرعية',
      columns: [
        { header: 'العملية الفرعية', key: 'sub_process_name', width: 35 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'معادلة القياس', key: 'measurement_formula', width: 30 },
      ],
      data: subProcessKpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  exportMultiSheetToExcel(filename, sheets);
};

/**
 * Export Unit Goals to Excel
 */
export const exportUnitGoalsToExcel = (
  unitGoals: any[],
  kpis: any[] = [],
  filename = 'أهداف_الوحدات',
) => {
  const sheets: SheetConfig[] = [
    {
      sheetName: 'أهداف الوحدات',
      columns: [
        { header: 'اسم الهدف', key: 'name', width: 30 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الوحدة التنظيمية', key: 'unit_name', width: 25 },
        { header: 'الهدف الاستراتيجي', key: 'strategic_goal_name', width: 25 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'حالة الإنجاز', key: 'achievement_status_display', width: 15 },
      ],
      data: unitGoals.map(goal => ({
        ...goal,
        achievement_status_display: getAchievementStatusDisplay(goal.achievement_status),
      })),
    },
  ];

  if (kpis.length > 0) {
    sheets.push({
      sheetName: 'المؤشرات',
      columns: [
        { header: 'هدف الوحدة', key: 'unit_goal_name', width: 30 },
        { header: 'الوحدة التنظيمية', key: 'unit_name', width: 25 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
      ],
      data: kpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  exportMultiSheetToExcel(filename, sheets);
};

/**
 * Export Support Tickets to Excel
 */
export const exportTicketsToExcel = (tickets: import('../types').SupportTicket[], filename = 'التذاكر') =>
  exportToExcel({
    filename,
    sheetName: 'التذاكر',
    rtl: true,
    columns: [
      { header: 'الرقم', key: 'ticket_number', width: 15 },
      { header: 'الموضوع', key: 'subject', width: 35 },
      { header: 'التفاصيل', key: 'description', width: 50 },
      { header: 'الأهمية', key: 'priority', width: 12 },
      { header: 'الحالة', key: 'status_display', width: 18 },
      { header: 'التاريخ', key: 'created_at', width: 18 },
    ],
    data: tickets,
  });

/**
 * Export Risks to Excel
 */
export const exportRisksToExcel = (risks: any[], filename = 'المخاطر') => {
  exportToExcel({
    filename,
    sheetName: 'المخاطر',
    columns: [
      { header: 'الرمز', key: 'code', width: 12 },
      { header: 'اسم المخاطرة', key: 'name', width: 35 },
      { header: 'نوع المخاطرة', key: 'risk_type_display', width: 18 },
      { header: 'النوع المرتبط', key: 'related_type_display', width: 20 },
      { header: 'العنصر المرتبط', key: 'related_name', width: 30 },
      { header: 'الاحتمالية', key: 'probability', width: 12 },
      { header: 'الأثر', key: 'impact', width: 12 },
      { header: 'قيمة المخاطرة', key: 'risk_value', width: 14 },
      { header: 'مستوى المخاطرة', key: 'risk_level_display', width: 18 },
      { header: 'الأسباب الداخلية', key: 'internal_causes', width: 40 },
      { header: 'الأسباب الخارجية', key: 'external_causes', width: 40 },
      { header: 'إجراءات التخفيف', key: 'mitigation_actions', width: 40 },
      { header: 'حالة التخفيف', key: 'mitigation_status', width: 18 },
      { header: 'قيمة المخاطرة بعد الضوابط', key: 'risk_value_after_controls', width: 22 },
      { header: 'الأثر المتوقع', key: 'expected_impact', width: 35 },
      { header: 'مالك المخاطرة', key: 'risk_owner', width: 25 },
    ],
    data: risks,
  });
};

// ============================================================
// Helper functions for display values
// ============================================================

const getGoalStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    draft: 'مسودة',
    pending: 'بانتظار الموافقة',
    approved: 'معتمد',
    rejected: 'مرفوض',
  };
  return statusMap[status] || status;
};

const getInitiativeStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    not_started: 'لم تبدأ',
    in_progress: 'قيد التنفيذ',
    completed: 'مكتملة',
    on_hold: 'متوقفة',
    cancelled: 'ملغاة',
  };
  return statusMap[status] || status;
};

const getFrequencyDisplay = (frequency: string): string => {
  const frequencyMap: Record<string, string> = {
    daily: 'يومي',
    weekly: 'أسبوعي',
    monthly: 'شهري',
    quarterly: 'ربع سنوي',
    semi_annual: 'نصف سنوي',
    annual: 'سنوي',
  };
  return frequencyMap[frequency] || frequency;
};

const getAchievementStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    excellent: 'ممتاز',
    good: 'جيد',
    acceptable: 'مقبول',
    poor: 'ضعيف',
  };
  return statusMap[status] || status;
};

export default {
  exportToExcel,
  exportGoalsToExcel,
  exportInitiativesToExcel,
  exportKPIsToExcel,
  exportKPIReadingsToExcel,
  exportEmployeesToExcel,
  exportOrganizationsToExcel,
  exportServicesToExcel,
  exportProcessesToExcel,
  exportUnitGoalsToExcel,
  exportRisksToExcel,
  exportTicketsToExcel,
};
